// Turns an item's text into the working copy the classifier reads. The stored
// and displayed text is never touched: everything here builds new strings.
//
// The result depends only on ASCII once folding is done. Accents come off
// through canonical decomposition, which Unicode promises never to change for
// letters that already exist, and the few letters that do not decompose are
// folded by an explicit table. Nothing here consults a locale.

// Letters and signs canonical decomposition leaves alone.
const FOLD: Readonly<Record<string, string>> = {
  "ß": "ss", "æ": "ae", "œ": "oe", "ø": "o", "ł": "l", "đ": "d", "ð": "d", "þ": "th", "ı": "i",
  "½": " 1/2 ", "¼": " 1/4 ", "¾": " 3/4 ", "⅓": " 1/3 ", "⅔": " 2/3 ", "×": " x ",
  "‘": "'", "’": "'", "ʼ": "'", "`": "'", "´": "'",
};
const FOLDABLE = /[ßæœøłđðþı½¼¾⅓⅔×‘’ʼ`´]/g;

/** Words that end the head of a clause: "Parsley for garnish", "Cream at Pharmacy". */
export const CUT_WORDS: ReadonlySet<string> = new Set(["for", "at", "from", "with", "in"]);

/** Units dropped only straight after a number: "400 g", "2 tbsp". */
export const MEASURE_UNITS: ReadonlySet<string> = new Set([
  "g", "gr", "gram", "grams", "kg", "kilo", "kilos", "mg", "ml", "cl", "dl", "l", "litre", "litres",
  "liter", "liters", "oz", "lb", "lbs", "tsp", "tbsp", "teaspoon", "teaspoons", "tablespoon",
  "tablespoons", "cup", "cups", "pinch", "handful", "dozen", "bunch", "x",
]);

/**
 * Words that never make an item's head. Containers stay in the text, because
 * some of them mean something ("3 tins tomatoes"), but the head of "tea bags"
 * is still tea.
 */
export const NOISE_WORDS: ReadonlySet<string> = new Set([
  "tin", "tins", "can", "cans", "jar", "jars", "pack", "packs", "packet", "packets", "bag", "bags",
  "bottle", "bottles", "box", "boxes", "carton", "cartons", "punnet", "punnets", "tub", "tubs",
  "large", "small", "medium", "big", "jumbo", "mini", "xl", "organic", "bio", "free", "range",
  "each", "please", "extra",
]);

// Bounds on the work one item can cause. An item's text may be 1,000
// characters; nothing a person shops for needs more than a handful of words.
export const MAX_WORDS = 48;
export const MAX_SEGMENTS = 8;
export const MAX_ALTERNATIVES = 6;

const TOKEN = /\d+(?:[.,]\d+)?(?:\/\d+)?[a-z]*|[a-z]+\d*|[()[\]{},;/&]/g;
const NUMBER = /^\d+(?:[.,]\d+)?(?:\/\d+)?$/;
const NUMBER_WITH_UNIT = /^\d+(?:[.,]\d+)?(?:\/\d+)?([a-z]+)$/;
const TIMES_NUMBER = /^x\d+$/;

export type Alternative = readonly string[];
export type Segment = readonly Alternative[];

export interface ParsedItem {
  /** The head clause, before any cut word, split on "or". */
  readonly head: Segment;
  /** Trailing clauses, then the words after a cut, then brackets: tried in that order. */
  readonly fallbacks: readonly Segment[];
  /** Every word of the trailing clauses, where a qualifier such as "frozen" may sit. */
  readonly trailing: readonly string[];
}

/** Lower case, accents off, and every character outside the grammar made a space. */
export function fold(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(FOLDABLE, (ch) => FOLD[ch] ?? " ")
    .replace(/'/g, "")
    .replace(/[^a-z0-9()[\]{},;/&.]/g, " ");
}

/** Words and structural signs, at most MAX_WORDS words. */
export function tokenise(text: string): string[] {
  const tokens: string[] = [];
  let words = 0;
  for (const match of fold(text).matchAll(TOKEN)) {
    const token = match[0];
    if (/^[a-z0-9]/.test(token)) {
      if (words === MAX_WORDS) break;
      words += 1;
    }
    tokens.push(token);
  }
  return tokens;
}

/**
 * Drops numbers, numbers with a unit attached, and a unit straight after a
 * number. Containers and every other word stay. Never strips to nothing: an
 * item that is only a number keeps it.
 */
export function stripQuantities(words: readonly string[]): string[] {
  const kept: string[] = [];
  let afterNumber = false;
  for (let i = 0; i < words.length; i += 1) {
    const word = words[i] as string;
    if (NUMBER.test(word)) {
      afterNumber = true;
      continue;
    }
    const unit = NUMBER_WITH_UNIT.exec(word)?.[1];
    if (unit !== undefined && MEASURE_UNITS.has(unit)) {
      afterNumber = false;
      continue;
    }
    if (TIMES_NUMBER.test(word)) continue;
    if (afterNumber && MEASURE_UNITS.has(word)) {
      afterNumber = false;
      continue;
    }
    if (word === "x" && NUMBER.test(words[i + 1] ?? "")) continue;
    afterNumber = false;
    kept.push(word);
  }
  return kept.length > 0 ? kept : [...words];
}

function alternatives(words: readonly string[]): Alternative[] {
  const out: Alternative[] = [];
  let current: string[] = [];
  const close = () => {
    let from = 0;
    let to = current.length;
    while (from < to && current[from] === "and") from += 1;
    while (to > from && current[to - 1] === "and") to -= 1;
    const trimmed = current.slice(from, to);
    if (trimmed.length > 0 && out.length < MAX_ALTERNATIVES) out.push(stripQuantities(trimmed));
    current = [];
  };
  for (const word of words) {
    if (word === "or") close();
    else current.push(word);
  }
  close();
  return out;
}

const OPEN = new Set(["(", "[", "{"]);
const CLOSE = new Set([")", "]", "}"]);

/** Splits an item's text into its head and the places a fallback may be found. */
export function parseItem(text: string): ParsedItem {
  const clauses: string[][] = [[]];
  const brackets: string[][] = [];
  let depth = 0;
  for (const token of tokenise(text)) {
    if (OPEN.has(token)) {
      if (depth === 0) brackets.push([]);
      depth += 1;
      continue;
    }
    if (CLOSE.has(token)) {
      if (depth > 0) depth -= 1;
      continue;
    }
    const into = depth > 0 ? (brackets[brackets.length - 1] as string[]) : (clauses[clauses.length - 1] as string[]);
    if (token === "," || token === ";") {
      // Inside brackets a comma lists examples; outside it starts a clause.
      if (depth > 0) into.push("or");
      else clauses.push([]);
    } else if (token === "/") {
      into.push("or");
    } else if (token === "&") {
      into.push("and");
    } else {
      into.push(token);
    }
  }

  const first = clauses[0] as string[];
  const cut = first.findIndex((word, i) => i > 0 && CUT_WORDS.has(word));
  const headWords = cut === -1 ? first : first.slice(0, cut);
  const afterCut = cut === -1 ? [] : first.slice(cut + 1);
  const trailingClauses = clauses.slice(1).filter((clause) => clause.length > 0);

  const fallbacks = [...trailingClauses, afterCut, ...brackets]
    .map(alternatives)
    .filter((segment) => segment.length > 0)
    .slice(0, MAX_SEGMENTS);

  return {
    head: alternatives(headWords),
    fallbacks,
    trailing: trailingClauses.flat(),
  };
}
